import React, { useRef } from 'react'
import type { CropRect } from '@renderer/utils/kotakCrop'

type Corner = 'nw' | 'ne' | 'sw' | 'se'

interface CropEditorProps {
  /** Rect to render, normalized [0,1] in this editor's DISPLAY space (== its own box). */
  rect: CropRect
  /**
   * Enforced box aspect in DISPLAY-NORMALIZED units (boxWidthNorm / boxHeightNorm). This is
   * `finalRatio / displayAspect` so the on-screen box reads as the chosen photo ratio even
   * though the pane itself may not be square.
   */
  boxAspect: number
  /** Fired continuously during drag with the new display-space rect (already clamped). */
  onChange: (rect: CropRect) => void
  label?: string
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)

/**
 * Move + ratio-locked resize handles for one kotak crop box, operating in the normalized
 * DISPLAY space of its container (absolute inset-0 over a camera pane). Emits display-space
 * rects; the parent converts to/from unrotated sensor space.
 */
const CropEditor: React.FC<CropEditorProps> = ({ rect, boxAspect, onChange, label }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{
    mode: 'move' | Corner
    startX: number
    startY: number
    startRect: CropRect
  } | null>(null)

  const toNorm = (clientX: number, clientY: number): { x: number; y: number } => {
    const el = containerRef.current
    if (!el) return { x: 0, y: 0 }
    const b = el.getBoundingClientRect()
    return {
      x: b.width > 0 ? (clientX - b.left) / b.width : 0,
      y: b.height > 0 ? (clientY - b.top) / b.height : 0
    }
  }

  const beginDrag = (
    e: React.PointerEvent,
    mode: 'move' | Corner
  ): void => {
    e.preventDefault()
    e.stopPropagation()
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, startRect: rect }
  }

  const onPointerMove = (e: React.PointerEvent): void => {
    const drag = dragRef.current
    if (!drag) return
    const el = containerRef.current
    if (!el) return
    const b = el.getBoundingClientRect()
    const dx = b.width > 0 ? (e.clientX - drag.startX) / b.width : 0
    const dy = b.height > 0 ? (e.clientY - drag.startY) / b.height : 0
    const s = drag.startRect

    if (drag.mode === 'move') {
      const x = Math.min(1 - s.w, Math.max(0, s.x + dx))
      const y = Math.min(1 - s.h, Math.max(0, s.y + dy))
      onChange({ ...s, x, y })
      return
    }

    // Resize: keep the OPPOSITE corner fixed, enforce boxAspect.
    const corner = drag.mode
    const anchorX = corner === 'nw' || corner === 'sw' ? s.x + s.w : s.x
    const anchorY = corner === 'nw' || corner === 'ne' ? s.y + s.h : s.y
    const p = toNorm(e.clientX, e.clientY)

    // Candidate size from pointer, then reconcile to the locked aspect by taking whichever
    // axis demands the larger box (feels responsive in both directions).
    const wFromP = Math.abs(clamp01(p.x) - anchorX)
    const hFromP = Math.abs(clamp01(p.y) - anchorY)
    let w = wFromP
    let h = w / boxAspect
    if (hFromP > h) {
      h = hFromP
      w = h * boxAspect
    }

    // Keep the box inside [0,1]² without breaking aspect: shrink to the tightest room the
    // drag direction allows from the fixed anchor.
    const roomX = corner === 'nw' || corner === 'sw' ? anchorX : 1 - anchorX
    const roomY = corner === 'nw' || corner === 'ne' ? anchorY : 1 - anchorY
    if (w > roomX) {
      w = roomX
      h = w / boxAspect
    }
    if (h > roomY) {
      h = roomY
      w = h * boxAspect
    }
    const minSide = 0.05
    if (w < minSide) {
      w = minSide
      h = w / boxAspect
    }

    const x = corner === 'nw' || corner === 'sw' ? anchorX - w : anchorX
    const y = corner === 'nw' || corner === 'ne' ? anchorY - h : anchorY
    onChange({ x: clamp01(x), y: clamp01(y), w, h })
  }

  const endDrag = (e: React.PointerEvent): void => {
    if (dragRef.current) {
      ;(e.target as Element).releasePointerCapture?.(e.pointerId)
      dragRef.current = null
    }
  }

  const pct = (v: number): string => `${(v * 100).toFixed(3)}%`
  const handleBase =
    'absolute w-6 h-6 bg-white border-2 border-[#3d2b1f] rounded-sm shadow -translate-x-1/2 -translate-y-1/2'

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 touch-none select-none"
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {/* Dim outside the crop with four bands (kept simple vs an SVG mask). */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-0 right-0 top-0 bg-black/55" style={{ height: pct(rect.y) }} />
        <div
          className="absolute left-0 right-0 bg-black/55"
          style={{ top: pct(rect.y + rect.h), bottom: 0 }}
        />
        <div
          className="absolute left-0 bg-black/55"
          style={{ top: pct(rect.y), height: pct(rect.h), width: pct(rect.x) }}
        />
        <div
          className="absolute bg-black/55"
          style={{ top: pct(rect.y), height: pct(rect.h), left: pct(rect.x + rect.w), right: 0 }}
        />
      </div>

      {/* The crop box */}
      <div
        className="absolute border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.6)] cursor-move"
        style={{ left: pct(rect.x), top: pct(rect.y), width: pct(rect.w), height: pct(rect.h) }}
        onPointerDown={(e) => beginDrag(e, 'move')}
      >
        {/* rule-of-thirds guides */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-0 right-0 border-t border-white/30" />
          <div className="absolute top-2/3 left-0 right-0 border-t border-white/30" />
          <div className="absolute left-1/3 top-0 bottom-0 border-l border-white/30" />
          <div className="absolute left-2/3 top-0 bottom-0 border-l border-white/30" />
        </div>
        {label && (
          <span className="absolute -top-7 left-0 text-xs font-semibold text-white bg-[#3d2b1f]/90 px-2 py-0.5 rounded">
            {label}
          </span>
        )}
      </div>

      {/* Corner handles (positioned on the box corners) */}
      {(['nw', 'ne', 'sw', 'se'] as Corner[]).map((corner) => {
        const left = corner === 'nw' || corner === 'sw' ? rect.x : rect.x + rect.w
        const top = corner === 'nw' || corner === 'ne' ? rect.y : rect.y + rect.h
        const cursor =
          corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize'
        return (
          <div
            key={corner}
            className={handleBase}
            style={{ left: pct(left), top: pct(top), cursor }}
            onPointerDown={(e) => beginDrag(e, corner)}
          />
        )
      })}
    </div>
  )
}

export default CropEditor
